/**
 * resolvePostLoginRedirect — consume-time guard for sessionStorage's
 * `loginRedirect` (Issue #1562, epic #1518).
 *
 * A stored redirect is honored only when it is a real deep link (#335
 * shareable links). A learner-home alias (/dashboard, /messenger) stored
 * by a pre-flip bounce is navigation residue, not intent — honoring it
 * sends every pre-flip learner to the old home exactly once after the org
 * flips `messenger_enabled` (staging human-test #99, tp-1521-01).
 *
 * Context-free on purpose (no React imports), mirroring resolveLearnerHome.
 */

import {
  resolveLearnerHome,
  MESSENGER_HOME,
  DASHBOARD_HOME,
} from './resolveLearnerHome';
import { isInternalPath } from './isInternalPath';

const HOME_ALIASES = new Set([MESSENGER_HOME, DASHBOARD_HOME]);

/**
 * @param {string|null|undefined} storedRedirect sessionStorage 'loginRedirect'
 * @param {object|null|undefined} branding login-response branding (raw wire
 *   shape) or BrandingContext value — same contract as resolveLearnerHome
 * @returns {string} the post-login navigation target
 */
export function resolvePostLoginRedirect(storedRedirect, branding) {
  const home = resolveLearnerHome(branding);
  if (!storedRedirect) return home;
  const normalized = storedRedirect.length > 1
    ? storedRedirect.replace(/\/+$/, '')
    : storedRedirect;
  return HOME_ALIASES.has(normalized) ? home : storedRedirect;
}

/**
 * isSafeRelativeRedirect — open-redirect guard for the magic-link `redirect`
 * query param (#1857). The magic-link email itself is not a trust boundary
 * (anyone reading `docs/SERVICES.md` levels of trust already trust the
 * inbox), but the value still round-trips through a URL and must be proven
 * safe again at the point of use, per the pinned decision: same-origin,
 * relative path only.
 *
 * Rejects: non-strings, empty strings, anything not starting with a single
 * '/' (so absolute URLs and bare hostnames are out), '//...' (protocol-
 * relative — browsers treat `//evil.com` as `https://evil.com`), and any
 * path containing a backslash (some browsers normalize '/\evil.com' the
 * same way), or any ASCII control character (the URL parser strips tab/LF/CR,
 * so '/\t/evil.com' becomes '//evil.com').
 */
export function isSafeRelativeRedirect(path) {
  // #4556: delegate to the stricter same-origin rule rather than keep a second
  // copy. The old local copy missed ASCII control chars: '/\t/evil.com' (from
  // `?returnTo=/%09/evil.com`) passed it, and the URL parser strips the tab
  // to '//evil.com'.
  return isInternalPath(path);
}

/**
 * resolveVerifyRedirect — VerifyPage's full redirect resolution (#1857).
 *
 * The magic-link `redirect` query param (set only by the "Keep this plan"
 * capture-email flow today, carrying a deep link like `/chat/123`) wins
 * over the sessionStorage `loginRedirect` residue when present and safe —
 * it survives cross-tab/cross-device opens, unlike sessionStorage. Absent
 * a valid link redirect, behaviour falls through unchanged to
 * resolvePostLoginRedirect, so this is byte-identical to the pre-#1857
 * flow whenever the link carries no `redirect` param.
 *
 * @param {string|null|undefined} linkRedirect the `redirect` URL query param
 * @param {string|null|undefined} storedRedirect sessionStorage 'loginRedirect'
 * @param {object|null|undefined} branding same contract as resolvePostLoginRedirect
 * @returns {string} the post-verify navigation target
 */
export function resolveVerifyRedirect(linkRedirect, storedRedirect, branding) {
  if (isSafeRelativeRedirect(linkRedirect)) return linkRedirect;
  return resolvePostLoginRedirect(storedRedirect, branding);
}
